import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  XMarkIcon,
  UserCircleIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";
import { Button, CountryFlag } from "@/shared/ui";
import { getCountryName, formatDate } from "@/shared/core";
import type { ConsultationWithDetails } from "../types/consultation.types";
import { analytics } from "@/utils/analytics";

interface ConsultationDetailModalProps {
  consultation: ConsultationWithDetails | null;
  isOpen: boolean;
  onClose: () => void;
  onConvertToCase?: (consultation: ConsultationWithDetails) => void;
  showConvertButton?: boolean;
}

const getStatusBadge = (status: string, t: (k: string) => string) => {
  const base = "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium";
  switch (status) {
    case "pending":
      return (
        <span className={`${base} bg-dorado/20 text-dorado border border-dorado/30`}>
          {t("lawyer.consultations.status.pending")}
        </span>
      );
    case "accepted":
      return (
        <span className={`${base} bg-atlantis-100 text-atlantis-800`}>
          {t("lawyer.consultations.status.accepted")}
        </span>
      );
    case "rejected":
      return (
        <span className={`${base} bg-red-100 text-red-800`}>
          {t("lawyer.consultations.status.rejected")}
        </span>
      );
    case "completed":
      return (
        <span className={`${base} bg-tiber-100 text-tiber-800`}>
          {t("lawyer.consultations.status.completed")}
        </span>
      );
    case "cancelled":
      return (
        <span className={`${base} bg-tiber-100 text-tiber-700`}>
          {t("lawyer.consultations.status.cancelled")}
        </span>
      );
    default:
      return null;
  }
};

const getUrgencyBadge = (urgency: string, t: (k: string) => string) => {
  const base = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
  switch (urgency) {
    case "urgent":
      return <span className={`${base} bg-red-100 text-red-800`}>{t("lawyer.consultations.urgency.urgent")}</span>;
    case "high":
      return <span className={`${base} bg-orange-100 text-orange-800`}>{t("lawyer.consultations.urgency.high")}</span>;
    case "normal":
      return <span className={`${base} bg-tiber-100 text-tiber-800`}>{t("lawyer.consultations.urgency.normal")}</span>;
    case "low":
      return <span className={`${base} bg-tiber-100 text-tiber-800`}>{t("lawyer.consultations.urgency.low")}</span>;
    default:
      return null;
  }
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

export const ConsultationDetailModal: React.FC<ConsultationDetailModalProps> = ({
  consultation,
  isOpen,
  onClose,
  onConvertToCase,
  showConvertButton = true,
}) => {
  const { t } = useTranslation();
  const hasTrackedReceived = useRef<Set<string>>(new Set());

  // Track when lawyer views a pending consultation (public_consultation_received)
  useEffect(() => {
    if (isOpen && consultation?.id && consultation?.status === "pending") {
      if (!hasTrackedReceived.current.has(consultation.id)) {
        hasTrackedReceived.current.add(consultation.id);
        analytics.publicConsultationReceived();
      }
    }
  }, [isOpen, consultation?.id, consultation?.status]);

  if (!consultation) return null;

  const clientName = consultation.client_profiles?.profiles?.full_name || "N/A";
  const clientEmail = consultation.client_profiles?.profiles?.email;
  const clientCountry = consultation.client_profiles?.profiles?.country;
  const profileImage = consultation.client_profiles?.profiles?.profile_image_url;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <div className="absolute inset-0 bg-juridico/40 backdrop-blur-sm" />
          <motion.div
            className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden z-10 flex flex-col"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-linear-to-r from-tiber-600 to-tiber-700 px-6 py-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt={clientName}
                      className="h-14 w-14 rounded-xl object-cover border-2 border-white/30"
                    />
                  ) : (
                    <div className="h-14 w-14 rounded-xl bg-white/20 flex items-center justify-center border-2 border-white/30">
                      <span className="text-white font-bold text-lg">
                        {getInitials(clientName)}
                      </span>
                    </div>
                  )}
                  <div>
                    <h2 className="text-xl font-bold text-white">{clientName}</h2>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {getStatusBadge(consultation.status, t)}
                      {getUrgencyBadge(consultation.urgency, t)}
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                        {t(`lawyer.consultations.practiceAreas.${consultation.practice_area}`, consultation.practice_area)}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                  aria-label="Close"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Description */}
              <section>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-titan uppercase tracking-wider mb-2">
                  <ChatBubbleLeftRightIcon className="w-4 h-4" />
                  {t("lawyer.consultationDetails.description")}
                </h3>
                <p className="text-tiber-800 leading-relaxed bg-romance-50 rounded-xl p-4 border border-tiber-100">
                  {consultation.description}
                </p>
              </section>

              {/* Client info */}
              <section>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-titan uppercase tracking-wider mb-3">
                  <UserCircleIcon className="w-4 h-4" />
                  {t("lawyer.consultationDetails.clientInfo")}
                </h3>
                <div className="bg-romance-50 rounded-xl p-4 border border-tiber-100 space-y-2">
                  <p className="text-juridico font-medium">{clientName}</p>
                  {clientEmail && (
                    <p className="text-sm text-tiber-700">
                      <a href={`mailto:${clientEmail}`} className="text-tiber-600 hover:text-tiber-800 hover:underline">
                        {clientEmail}
                      </a>
                    </p>
                  )}
                  {clientCountry && (
                    <div className="flex items-center gap-2 text-sm text-tiber-700">
                      <CountryFlag countryCode={clientCountry} size="1.25em" />
                      {getCountryName(clientCountry)}
                    </div>
                  )}
                  {consultation.preferred_contact && (
                    <p className="text-sm text-titan">
                      {t("lawyer.consultations.preferredContact")}: {t(`lawyer.consultations.contactMethods.${consultation.preferred_contact}`, consultation.preferred_contact)}
                    </p>
                  )}
                </div>
              </section>

              {/* Timeline */}
              <section>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-titan uppercase tracking-wider mb-3">
                  <CalendarIcon className="w-4 h-4" />
                  {t("lawyer.consultationDetails.timeline.label")}
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-2 border-b border-tiber-100">
                    <span className="text-titan">{t("lawyer.consultationDetails.createdAt")}</span>
                    <span className="text-juridico font-medium">{formatDate(consultation.created_at)}</span>
                  </div>
                  {consultation.responded_at && (
                    <div className="flex justify-between py-2 border-b border-tiber-100">
                      <span className="text-titan">{t("lawyer.consultationDetails.respondedAt")}</span>
                      <span className="text-juridico font-medium">{formatDate(consultation.responded_at)}</span>
                    </div>
                  )}
                </div>
              </section>
            </div>

            {/* Footer */}
            <div className="border-t border-tiber-200 p-4 bg-romance-50 flex justify-end gap-3">
              <Button variant="secondary" onClick={onClose}>
                {t("common.close")}
              </Button>
              {showConvertButton && consultation.status === "accepted" && onConvertToCase && (
                <Button
                  variant="primary"
                  leftIcon={<BriefcaseIcon className="w-5 h-5" />}
                  onClick={() => {
                    onConvertToCase(consultation);
                    onClose();
                  }}
                >
                  {t("lawyer.consultations.convertToCase")}
                </Button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
